"use client";

import { CSSProperties } from "react";
import { cn } from "~/lib/utils";

type RollingNumberProps = {
    value: string;
    animate?: boolean;
    className?: string;
};

const DIGIT_CYCLE = "0123456789";

function isDigit(char: string) {
    return char >= "0" && char <= "9";
}

function digitFromCycle(index: number) {
    return DIGIT_CYCLE[index % DIGIT_CYCLE.length] ?? "0";
}

function getReelMotion(digitIndex: number) {
    return {
        spins: 2 + (digitIndex % 3),
        delayMs: digitIndex * 70,
        durationMs: 2100 + (digitIndex % 3) * 220 + (digitIndex % 2) * 100,
    };
}

function DigitReel({
    digit,
    digitIndex,
    animate,
}: {
    digit: string;
    digitIndex: number;
    animate: boolean;
}) {
    const target = Number(digit);
    const { spins, delayMs, durationMs } = getReelMotion(digitIndex);
    const steps = spins * DIGIT_CYCLE.length + target;

    return (
        <span className="relative inline-block h-[1lh] overflow-hidden align-baseline">
            <span className={cn(animate && "invisible")}>{digit}</span>
            <span className={cn("absolute inset-0 overflow-hidden", !animate && "invisible")}>
                <span
                    className={cn(
                        "flex flex-col [transform:translateY(0)] [transition:transform_var(--roll-duration)_cubic-bezier(0.25,0,0.05,1)_var(--roll-delay)]",
                        "motion-reduce:[transform:translateY(var(--final-y))] motion-reduce:[transition:none]",
                        animate && "[transform:translateY(var(--final-y))]",
                    )}
                    style={{
                        "--final-y": `-${steps}lh`,
                        "--roll-duration": `${durationMs}ms`,
                        "--roll-delay": `${delayMs}ms`,
                    } as CSSProperties}
                >
                    {Array.from({ length: steps + 1 }, (_, index) => (
                        <span
                            key={index}
                            className="flex h-[1lh] w-full items-center justify-center"
                        >
                            {digitFromCycle(index)}
                        </span>
                    ))}
                </span>
            </span>
        </span>
    );
}

export default function RollingNumber({
    value,
    animate = false,
    className,
}: RollingNumberProps) {
    const characters = Array.from(value);

    return (
        <span className={cn("inline-flex items-baseline whitespace-nowrap", className)}>
            <span className="sr-only">{value}</span>
            <span aria-hidden="true" className="inline-flex items-baseline whitespace-nowrap">
                {characters.map((char, index) => {
                    if (!isDigit(char)) {
                        return <span key={`${char}-${index}`}>{char}</span>;
                    }

                    const digitIndex = characters
                        .slice(0, index)
                        .filter(isDigit)
                        .length;

                    return (
                        <DigitReel
                            key={`${char}-${index}`}
                            digit={char}
                            digitIndex={digitIndex}
                            animate={animate}
                        />
                    );
                })}
            </span>
        </span>
    );
}
